/* External */
import {
  memo,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useParams } from 'react-router-dom';
import debounce from 'lodash-es/debounce';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

/* Ott-common */
import { MyListAssetType } from '@dtvgo/backend-service/normalize';

/* Providers */
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* Components */
import Buttons from './Buttons';
import ScrollView from '../../../../components/ScrollView';
import SeasonSelector from '../../../../components/SeasonSelector';
import EpisodeSelector from '../../../../components/EpisodeSelector';
import Tabs from '../../../../components/Tabs';
import AddRemoveWatchListButton from '../../../../components/AddRemoveWatchListButton';
import CardEpisodeSkeleton from '../../../../components/CardEpisodeSkeleton/CardEpisodeSkeleton';
import ContinueWatchingShowButton from '../../../../components/ContinueWatchingShowButton';
import MoreLikeThisVodEpg from '../../../../components/MoreLikeThisVodEpg/MoreLikeThisVodEpg';
import DetailsSelector from '../../../../components/DetailsSelector';
import FocusableButton from '../../../../components/FocusableButton';

/* Utils */
import {
  ImageRatio,
  DOT_SEPARATOR,
  REPORT_GA,
  RoutesOriginCarousels,
  STARTED_PAGE_ID,
} from '../../../../utils/constants';
import {
  isSpideoMoreLikeThisActive,
  timesPressedBackButtonOnPdp,
} from '../../../../utils/env';
import { noOp } from '../../../../utils/functions';
import useFetchShow from '../../../../hooks/useFetchShow';
import useFetchSeason from '../../../../hooks/useFetchSeason';
import useSeasonEpisodes from '../../../../hooks/useSeasonEpisodes';
import useShowSeasons from '../../../../hooks/useShowSeasons';
import { PLAY_SOURCE_PAGE } from '../../../../utils/trackSourceConviva';
import { redirectToPlayer, redirectTo } from '../../../../utils/history';
import { Direction } from '../../../../utils/spatialNavigation';
import { COMPONENTS } from '../../../../utils/focusKey';
import useFetchContinueWatchingShow from '../../../../hooks/useFetchContinueWatchingShow';
import {
  getAllGenres,
  getFormattedDuration,
  useFullEpisodeName,
  getRating,
  joinArrayWithSeparator,
  joinArrayWithSpace,
  isSelfClassified,
  isSports,
} from '../../../../utils/metaDataUtils';
import useContent from '../../../../hooks/useContent';
import { gaService } from '../../../../services/gaService';
import {
  formatContentData,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../../../utils/googleTags';
import { getLink, Routes } from '../../../../utils/router';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

/*  Storages */
import {
  increasePdpHistory,
  decreasePdpHistory,
  selectPdpHistory,
  increaseBackButtonHistory,
  selectBackButtonHistory,
} from '../../../../storage/pdpHistory';

/* Hooks */
import useImageCard from '../../../../hooks/useImageCard';

/* Style */
import './Show.scss';

const IMG_WIDTH = 1980;
const IMG_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;
const REQUIRED_PROPS_SHOW = ['title', 'description', 'genres', 'rating', 'type', 'ratingDescription', 'ratingWarning', 'images', 'seasons'];
const REQUIRED_PROPS_SEASON = ['seasonNumber', 'episodes'];

function Show({ realFocusKey = '', setFocus = noOp, sourceProvider }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const SEASONS_SELECTOR_FOCUS_KEY = `${realFocusKey}-seasons-selector`;
  const BUTTONS_CONTAINER_FOCUS_KEY = `${realFocusKey}-buttons-container`;
  const CONTINUE_WATCHING_FOCUS_KEY = `${realFocusKey}-continue-watching`;
  const BACK_HOME_BUTTON_KEY = `${realFocusKey}-back-home-button`;
  const [areEpisodesFocusable, setAreEpisodesFocusable] = useState(false);

  const history = useHistory();
  const { state: locationState } = useLocation();
  const {
    commonTaggingEvent,
    gtmChangeItem,
    gtmState: { pdpSelectedTabTitle, selectedSeason },
  } = useGtmLocalContext();
  const tagsMountRef = useRef(false);
  const seasonSelectedRef = useRef(false);

  const { showId } = useParams();
  const show = useFetchShow(showId, REQUIRED_PROPS_SHOW, sourceProvider);
  const seasonsList = useShowSeasons(show);
  const isThereSeason = seasonsList?.size > 0;
  const firstSeason = seasonsList?.get(0);
  const firstSeasonNumber = firstSeason?.seasonNumber;
  const firstEpisodeId = firstSeason?.episodes?.get(0);
  const firstEpisode = useContent(firstEpisodeId, sourceProvider);

  const { title, images } = show || {};

  const [selectedSeasonNumber, setSelectedSeasonNumber] = useState(firstSeasonNumber);

  const prevShowIdRef = useRef();

  const dispatch = useDispatch();
  const pdpHistory = useSelector(selectPdpHistory);
  const backButtonHistory = useSelector(selectBackButtonHistory);
  const shouldDisplayBackHomeButton = isSpideoMoreLikeThisActive && pdpHistory.length > 1;

  /* eslint-disable-next-line react-hooks/exhaustive-deps
  -- phdHistory causes an unwanted side effect */
  const shouldAddIdToPdpHistory = useMemo(() => !pdpHistory.includes(showId), [showId]);

  useEffect(() => {
    setSelectedSeasonNumber(firstSeasonNumber);
  }, [firstSeasonNumber]);

  const { season, isFetching } = useFetchSeason({
    showId,
    seasonNumber: selectedSeasonNumber,
    requiredProps: REQUIRED_PROPS_SEASON,
    sourceProvider,
  });

  const episodes = useSeasonEpisodes(season, sourceProvider);

  const debouncedSetSelectedSeasonNumber = useMemo(
    () => debounce(
      (newSelectedSeasonNumber) => {
        setSelectedSeasonNumber(newSelectedSeasonNumber);
        seasonSelectedRef.current = true;
      },
      1000,
    ),
    [],
  );

  const handleEpisodeArrowPress = useCallback((direction, { index }) => {
    if (direction === Direction.UP && index === 0) {
      setFocus(COMPONENTS.TABS_LIST);
      return false;
    }

    return direction !== Direction.RIGHT;
  }, [setFocus]);

  const handleSeasonSelect = useCallback((newSelectedSeasonNumber) => {
    if (newSelectedSeasonNumber !== selectedSeasonNumber) {
      setSelectedSeasonNumber(undefined);
      debouncedSetSelectedSeasonNumber(newSelectedSeasonNumber);
    }
  }, [debouncedSetSelectedSeasonNumber, selectedSeasonNumber]);

  const handleEpisodeRedirectToPlayer = useCallback(({ content }) => {
    redirectToPlayer(content, {
      page: PLAY_SOURCE_PAGE.PDP,
      section: title || '',
      state: locationState,
    });
  }, [locationState, title]);

  const handleBackHome = useCallback(() => {
    redirectTo(getLink(Routes.Private.Home));
  }, []);

  const handleArrowPressTabs = useCallback((direction) => {
    if (direction === Direction.UP) {
      setIsScrolled(false);
      setFocus(BUTTONS_CONTAINER_FOCUS_KEY);
      return false;
    }

    return true;
  }, [BUTTONS_CONTAINER_FOCUS_KEY, setFocus]);

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  useEffect(() => {
    setFocus(CONTINUE_WATCHING_FOCUS_KEY);
  }, [setFocus, showId, CONTINUE_WATCHING_FOCUS_KEY]);

  const handleWheel = useCallback((event) => {
    setIsScrolled(event.deltaY > 0);
  }, []);

  const handleBecameFocused = useCallback(() => {
    setIsScrolled(false);
  }, []);

  const handleTabsBecameFocused = useCallback(() => {
    setAreEpisodesFocusable(false);
  }, []);

  const handleSeasonsBecameFocused = useCallback(() => {
    setAreEpisodesFocusable(true);
  }, []);

  const {
    contentId: episodeId,
    content: episode,
  } = useFetchContinueWatchingShow(showId, sourceProvider);
  const isThereContinueWatchingEpisode = !!episodeId;

  const metadata = useMemo(() => {
    if (isThereContinueWatchingEpisode) {
      return getFormattedDuration(episode);
    }

    return joinArrayWithSeparator([
      show?.releaseYear,
      getAllGenres(show, DOT_SEPARATOR),
    ], DOT_SEPARATOR);
  }, [episode, isThereContinueWatchingEpisode, show]);

  const titleEpisode = useFullEpisodeName(episode, DOT_SEPARATOR);

  const subtitle = useMemo(() => joinArrayWithSpace([
    getRating(show),
    metadata,
  ]), [metadata, show]);

  const focusDefaultTab = useMemo(() => {
    if (!prevShowIdRef.current && show?.vrioAssetId) {
      prevShowIdRef.current = show.vrioAssetId;
    }
    if (show?.vrioAssetId && prevShowIdRef.current !== show?.vrioAssetId) {
      prevShowIdRef.current = show.vrioAssetId;
      return isScrolled;
    }

    return false;
  }, [show?.vrioAssetId, isScrolled]);

  const pageId = sessionStorage.getItem(STARTED_PAGE_ID);

  const handleOnBack = useCallback(() => {
    if (backButtonHistory === timesPressedBackButtonOnPdp) {
      redirectTo(getLink(RoutesOriginCarousels[pageId]));
      return;
    }

    dispatch(increaseBackButtonHistory());
    dispatch(decreasePdpHistory(showId));

    history.goBack();
  }, [backButtonHistory, dispatch, pageId, history, showId]);

  useOnBackPress({
    listener: handleOnBack,
    when: isSpideoMoreLikeThisActive,
  });

  useEffect(() => {
    setIsScrolled(false);

    if (isSpideoMoreLikeThisActive && shouldAddIdToPdpHistory) {
      dispatch(increasePdpHistory(showId));
    }
  }, [showId, dispatch, shouldAddIdToPdpHistory]);

  useEffect(() => {
    gaService.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_PDP_VIEW_PDP_PAGE,
      label: REPORT_GA.GA_EVENT_LABEL_PDP_VIEW_PDP_PAGE,
      category: REPORT_GA.GA_EVENT_CATEGORY_PDP,
    });
  }, []);

  const pdpCommonTagEvent = useCallback(({ eventName, subcategory, content }) => {
    if (eventName === GTM_EVENT_NAMES.PDP.WATCH_CONTENT) {
      tagsMountRef.current = false;
    }
    const {
      collectionName,
      componentType,
      gtmPlaySource,
      providerName,
      sectionName,
      transmissionType,
    } = locationState;
    const episodeContent = content || firstEpisode;
    const episodeHasAgeRating = !!episodeContent?.rating;
    const currentContent = episodeHasAgeRating ? episodeContent : show;

    commonTaggingEvent({
      category: GTM_CATEGORIES.PDP_MOVIE_SERIE_LIVE_DATA,
      subCategory: subcategory || GTM_SUBCATEGORIES.PDP.SERIE_WATCH,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content: episodeContent,
            rest: {
              progress: episodeContent?.progress,
              playSource: gtmPlaySource,
              isLive: false,
              rating: currentContent?.rating,
            },
          }),
        },
        componentType,
        transmissionType: transmissionContentType(transmissionType),
        sectionName,
        collectionName,
        providerName,
        isSports: isSports(episodeContent),
        playerType: playerType(episodeContent?.type, false, isSports(episodeContent)),
        filterPdp: pdpSelectedTabTitle,
        text: selectedSeason || '',
      },
    });
  }, [
    commonTaggingEvent,
    firstEpisode,
    locationState,
    pdpSelectedTabTitle,
    selectedSeason,
    show,
  ]);

  useEffect(() => {
    if (!tagsMountRef.current && firstEpisode && show) {
      pdpCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = true;
    }
  }, [firstEpisode, pdpCommonTagEvent, show]);

  useEffect(() => {
    if (isScrolled && pdpSelectedTabTitle) {
      pdpCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PDP.FILTER_TAB_PDP,
        subcategory: GTM_SUBCATEGORIES.PDP.SERIE_SEASONS_AND_RELATED_CONTENT,
      });
    }
  }, [isScrolled, pdpCommonTagEvent, pdpSelectedTabTitle]);

  useEffect(() => {
    if (!isScrolled && pdpSelectedTabTitle) {
      gtmChangeItem({ pdpSelectedTabTitle: undefined });
    }
  }, [isScrolled, gtmChangeItem, pdpSelectedTabTitle]);

  useEffect(() => {
    if (seasonSelectedRef.current) {
      pdpCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PDP.SELECT_SEASON,
        subcategory: GTM_SUBCATEGORIES.PDP.SERIE_SEASONS_AND_RELATED_CONTENT,
      });
      seasonSelectedRef.current = false;
    }
  }, [pdpCommonTagEvent]);

  const handleSeasonSelectAndTagEvent = useCallback((newSelectedSeasonNumber) => {
    handleSeasonSelect(newSelectedSeasonNumber);
  }, [handleSeasonSelect]);

  const handleEpisodeEnterPress = useCallback(({ content }) => {
    handleEpisodeRedirectToPlayer({ content });
    pdpCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PDP.WATCH_CONTENT,
      subcategory: GTM_SUBCATEGORIES.PDP.SERIE_SEASONS_AND_RELATED_CONTENT,
      content,
    });
  }, [handleEpisodeRedirectToPlayer, pdpCommonTagEvent]);

  return (
    <div
      className={`dtv-html5-c-detail-serie ${isScrolled ? 'dtv-html5-c-detail-serie--scrolled' : ''}`}
      onWheel={isThereSeason ? handleWheel : undefined}
    >
      <img className="dtv-html5-c-detail-serie__image" src={imageUrl} alt={title} />
      <div className={`dtv-html5-c-detail-serie__container ${isThereContinueWatchingEpisode ? 'dtv-html5-c-detail-serie__container--less-margin-top' : ''}`}>
        {shouldDisplayBackHomeButton && (
          <div className="dtv-html5-c-detail-serie__content-back-home-button">
            <FocusableButton
              className="dtv-html5-c-detail-serie__back-home-button"
              onEnterPress={handleBackHome}
              focusKey={BACK_HOME_BUTTON_KEY}
              icon="arrow-back"
              primary
            >
              <FormattedMessage id="pdp.backButtonTitle" />
            </FocusableButton>
          </div>
        )}

        <div className="dtv-html5-c-detail-serie__content-title">
          <h1 className="dtv-html5-c-detail-serie__title">
            {title || ''}
          </h1>
        </div>
        {isThereContinueWatchingEpisode && (
          <h2 className="dtv-html5-c-detail-serie__title-episode">
            {titleEpisode}
          </h2>
        )}
        <p className="dtv-html5-c-detail-serie__metadata">
          {subtitle}
        </p>
        <p className="dtv-html5-c-detail-serie__description">
          {(isThereContinueWatchingEpisode ? episode?.description : show?.description) || ''}
        </p>
        <Buttons focusKey={BUTTONS_CONTAINER_FOCUS_KEY}>
          <ContinueWatchingShowButton
            focusKey={CONTINUE_WATCHING_FOCUS_KEY}
            episodeId={episodeId}
            episodeContent={episode}
            firstEpisode={firstEpisode}
            onBecameFocused={handleBecameFocused}
            focusRestorable={!isScrolled}
            locationState={locationState}
            pdpCommonTagEvent={pdpCommonTagEvent}
            blockUpArrow={!shouldDisplayBackHomeButton}
          />
          <AddRemoveWatchListButton
            contentId={showId}
            type={MyListAssetType.SERIE}
            vrioAssetId={show?.vrioAssetId}
            onBecameFocused={handleBecameFocused}
            focusRestorable={!isScrolled}
            content={firstEpisode}
            locationState={locationState}
            sourceProvider={sourceProvider}
          />
        </Buttons>
      </div>
      <Tabs
        onScrolledChange={setIsScrolled}
        showActiveTab={isScrolled}
        loadDefaultTab={isScrolled}
        onArrowPress={handleArrowPressTabs}
        onBecameFocused={handleTabsBecameFocused}
        focusDefaultTab={focusDefaultTab}
      >
        <div className="dtv-html5-c-detail-serie__content-episodes" data-labeltab="common.episodes">
          {isThereSeason ? (
            <>
              <div className="dtv-html5-c-detail-serie__seasons">
                <SeasonSelector
                  seasonsList={seasonsList}
                  focusKey={SEASONS_SELECTOR_FOCUS_KEY}
                  selectedSeasonNumber={selectedSeasonNumber}
                  focusRestorable={isScrolled}
                  onSeasonSelect={handleSeasonSelectAndTagEvent}
                  onScrolledChange={setIsScrolled}
                  onBecameFocused={handleSeasonsBecameFocused}
                />
              </div>
              {isFetching ? (
                <CardEpisodeSkeleton dark />
              ) : (
                <EpisodeSelector
                  episodes={episodes}
                  focusRestorable={isScrolled}
                  focusable={areEpisodesFocusable}
                  onEpisodeArrowPress={handleEpisodeArrowPress}
                  onEpisodeEnterPress={handleEpisodeEnterPress}
                />
              )}
            </>
          ) : (
            <div className="dtv-html5-c-detail-serie__not-episodes-message">
              <FormattedMessage id="detailSerie.thereAreNotContent" />
            </div>
          )}
        </div>
        {isSpideoMoreLikeThisActive && (
          <div
            className="dtv-html5-c-detail-serie__content-episodes"
            data-labeltab="tabs.title.related.content"
          >
            <ScrollView
              resetScrollPosition={!isScrolled}
              propagateEventOnReachTop
            >
              <MoreLikeThisVodEpg
                className="dtv-html5-c-detail-serie__more-like-this"
                contentId={showId}
                pdpCommonTagEvent={pdpCommonTagEvent}
                isDisplaying={isScrolled}
              />
            </ScrollView>
          </div>
        )}
        <div className="dtv-html5-c-detail-serie__content-episodes" data-labeltab="common.details">
          <div className="dtv-html5-c-detail-serie__seasons">
            <DetailsSelector
              title={title}
              description={show?.description}
              genres={show?.genres?.join(' • ')}
              ageRating={getRating(show)}
              rating={show?.rating}
              ratingDescription={(isSelfClassified(show) ? '' : show?.ratingDescription)}
              ratingWarning={(isSelfClassified(show) ? '' : show?.ratingWarning)}
              type={MyListAssetType.SERIE}
            />
          </div>
        </div>
      </Tabs>
    </div>
  );
}

Show.propTypes = {
  sourceProvider: PropTypes.string,
  realFocusKey: PropTypes.string,
  setFocus: PropTypes.func,
};

export default memo(withFocusable()(memo(Show)));
